import React, { useEffect, useRef, useState } from "react";
import styles from "./styles.module.scss";
import { IContentModel } from "models/dto/ZoomiLxp/Models/ContentModels/IContentModel";
import classNames from "classnames";
import isEmpty from "lodash/isEmpty";
import PlayerVideo from "../player-video/player-video";
import PlayerControl, { ControlParams } from "../player-control/player-control";
import ReactPlayer from "react-player/lazy";
import { Duration } from "luxon";
import { useAppStore } from "store";
import { EventTypes } from "models/dto/ZoomiLxp/Utilities/Enumerations/EventTypes";
import { usePlayerControl } from "hooks/usePlayerControl";
import { usePlayerNav } from "hooks/usePlayerNav";
import { ISaveEventCourseItemHistoryModel } from "models/dto/ZoomiLxp/Models/EventModels/ISaveEventCourseItemHistoryModel";
import { ISegmentPositions } from "../player-layout/player-layout";
import { ISetPositionPayload } from "models/dto/ZoomiLxp/Models/EventModels/Payloads/ISetPositionPayload";
import { IStartPayload } from "models/dto/ZoomiLxp/Models/EventModels/Payloads/IStartPayload";
import { IPlayerPropsBase } from "../data/data";
import { useContentDownload } from "hooks/useContentFiles";

interface IPlayerVideoProps extends IPlayerPropsBase {
	content: IContentModel;
	segment?: ISegmentPositions;
}

const PlayerVideoBox = ({ content, className, segment, onEventHandle }: IPlayerVideoProps) => {
	const { contentStore, coursesStore, playerStore, toasterStore, aiSummaryStore } = useAppStore();
	const downloadFile = useContentDownload(content);
	const currentCourseItemId = coursesStore.curActiveContent.courseItemId;
	const calledOnce = useRef(false);
	const calledOnceTwo = useRef(false);
	const [isFullscreen, setIsFullscreen] = useState(false);
	const [selectedSubtitleId, setSelectedSubtitleId] = useState<number>(0);
	const wasEndEventSentRef = useRef(false);
	const playingRef = useRef(false);
	const currentPositionRef = useRef(0);
	const sessionIdRef = useRef<number>(contentStore.sessionId ?? 0);
	const normalizePosition = (value?: number) => (typeof value === "number" ? Math.round(value * 100) / 100 : 0);

	const handlePlay = async () => {
		if (segment) {
			const currentProgress = normalizePosition(playerStore?.params?.currentProgress);
			const startPosition = normalizePosition(segment.startPosition);
			const endPosition = normalizePosition(segment.endPosition);
			if (currentProgress > 0 && (currentProgress < startPosition || currentProgress >= endPosition))
				return;
		}

		setParams((prevState: ControlParams) => ({ ...prevState, playing: true }));

		const data: ISaveEventCourseItemHistoryModel = {
			sessionId: contentStore?.sessionId ?? 0,
			eventType: EventTypes.Play,
			payload: JSON.stringify({
				currentPosition: params.playerRef?.current?.getCurrentTime(),
				isPlaying: true,
				currentPlaybackRate: params.speed!,
			}),
		};
		await onEventHandle(data);
		await contentStore.startKeepAlive();
	};

	const handlePause = async () => {
		setParams((prevState: ControlParams) => ({ ...prevState, playing: false }));
		const data: ISaveEventCourseItemHistoryModel = {
			sessionId: contentStore?.sessionId ?? 0,
			eventType: EventTypes.Pause,
			payload: JSON.stringify({
				currentPosition: params.playerRef?.current?.getCurrentTime(),
				isPlaying: false,
				currentPlaybackRate: params.speed!,
			}),
		};
		await onEventHandle(data);
	};

	const handleVolumeMute = () => {
		setParams((prevState: ControlParams) => ({ ...prevState, muted: true, volume: 0 }));
	};

	const handleVolumeUnmute = (volume?: number) => {
		setParams((prevState: ControlParams) => ({ ...prevState, muted: false, volume: volume ?? 0.5 }));
	};

	const handleStart = async () => {
		try {
			if (contentStore.sessionId) {
				const currentTime = normalizePosition(params.playerRef?.current?.getCurrentTime());
				const startPayload: IStartPayload = {
					currentPosition: currentTime || (segment ? normalizePosition(segment.startPosition) : 0),
					isPlaying: params.playing,
					currentPlaybackRate: params.speed ? params.speed : 1,
				};

				contentStore.sendEvent(EventTypes.Start, startPayload);
			}
		} catch (err) {
			toasterStore.showErrorMessage(err);
		}
	};

	const handleSeekChange = async (value: number) => {
		const normalizedValue = normalizePosition(value);
		if (segment) {
			const startPosition = normalizePosition(segment.startPosition);
			const endPosition = normalizePosition(segment.endPosition);
			if (normalizedValue < startPosition || normalizedValue > endPosition) return;
		}
		try {
			const currentTime = normalizePosition(params.playerRef?.current?.getCurrentTime());
			params.playerRef?.current?.seekTo(normalizedValue, "seconds");
			setParams((prevState: ControlParams) => ({ ...prevState, currentProgress: normalizedValue }));

			const setPositionPayload: ISetPositionPayload = {
				currentPosition: currentTime || 0,
				isPlaying: params.playing,
				currentPlaybackRate: params.speed ? params.speed : 1,
				newPosition: normalizedValue,
			};
			const data: ISaveEventCourseItemHistoryModel = {
				sessionId: contentStore?.sessionId ?? 0,
				eventType: EventTypes.SetPosition,
				payload: JSON.stringify(setPositionPayload),
			};

			await onEventHandle(data);
		} catch (err) {
			toasterStore.showErrorMessage(err);
		}
	};

	const handleVolumeLevel: React.FormEventHandler<HTMLInputElement> | undefined = (e) => {
		setParams((prevState: ControlParams) => ({
			...prevState,
			volume: Number((e.target as HTMLInputElement).value),
		}));
	};

	const getFormattedProgress = (seconds: number): string => {
		const duration = Duration.fromObject({ seconds });
		return duration.toFormat("h:mm:ss");
	};

	const handleSpeed = async (speed: number) => {
		setParams((prevState: ControlParams) => ({ ...prevState, speed }));

		const data: ISaveEventCourseItemHistoryModel = {
			sessionId: contentStore?.sessionId ?? 0,
			eventType: EventTypes.SetPlaybackRate,
			payload: JSON.stringify({
				currentPosition: params.playerRef?.current?.getCurrentTime(),
				currentPlaybackRate: params.playerRef?.current?.props.playbackRate,
				newPlaybackRate: speed,
				isPlaying: params.playing,
			}),
		};
		await onEventHandle(data);
	};
	const { prev, next } = usePlayerNav();

	const handlePrev = async () => {
		const data: ISaveEventCourseItemHistoryModel = {
			sessionId: contentStore?.sessionId ?? 0,
			eventType: EventTypes.End,
			payload: JSON.stringify({
				currentPosition: params.playerRef?.current?.getCurrentTime(),
				isPlaying: params.playing,
			}),
		};
		contentStore.stopKeepAlive();
		await onEventHandle(data).then(() => prev());
	};

	const handleNext = async () => {
		const data: ISaveEventCourseItemHistoryModel = {
			sessionId: contentStore?.sessionId ?? 0,
			eventType: EventTypes.End,
			payload: JSON.stringify({
				currentPosition: params.playerRef?.current?.getCurrentTime(),
				isPlaying: params.playing,
			}),
		};
		contentStore.stopKeepAlive();
		await onEventHandle(data).then(() => {
			next();
		});
	};

	const handleComplete = async () => {
		setParams((prevState: ControlParams) => ({ ...prevState, playing: false }));
		contentStore.stopKeepAlive();

		const data: ISaveEventCourseItemHistoryModel = {
			sessionId: contentStore?.sessionId ?? 0,
			eventType: EventTypes.Complete,
			payload: JSON.stringify({
				currentPosition: params.playerRef?.current?.getCurrentTime(),
				isPlaying: params.playing,
				currentPlaybackRate: params.speed!,
			}),
		};
		try {
			await onEventHandle(data);
			coursesStore.curActiveContent.completed = true;
		} catch (err) {
			toasterStore.showErrorMessage(err);
		}

		if (!coursesStore.isPartCourse) {
			aiSummaryStore.checkAndCloseSegmentAlert();
		}
		if (coursesStore.curPositionContent + 1 < coursesStore.countContents) coursesStore.curPositionContent++;
	};

	const initParams: ControlParams = {
		currentProgress: 1,
		totalProgress: 1,
		boxRef: useRef<HTMLDivElement>(null),
		playing: false,
		volume: 0.8,
		muted: false,
		playerRef: useRef<ReactPlayer>(null),
		speed: 1,
		isReady: false,
		handleStart,
		handlePlay,
		handlePause,
		handleVolumeMute,
		handleVolumeUnmute,
		handleSeekChange,
		handleVolumeLevel,
		getFormattedProgress,
		handleSpeed,
		handleNext,
		handlePrev,
		handleComplete,
		isDownload: false,
	};

	const sendEndEventOnce = async () => {
		if (wasEndEventSentRef.current) return;
		wasEndEventSentRef.current = true;

		const currentPosition = currentPositionRef.current;
		const isPlaying = playingRef.current;
		const sessionId = sessionIdRef.current;

		const data: ISaveEventCourseItemHistoryModel = {
			sessionId,
			eventType: EventTypes.End,
			payload: JSON.stringify({
				currentPosition,
				isPlaying,
			}),
		};

		contentStore.stopKeepAlive();

		await onEventHandle(data);
	};

	const [params, setParams] = React.useState<ControlParams>(initParams);

	useEffect(() => {
		playingRef.current = params.playing;
	}, [params.playing]);

	useEffect(() => {
		currentPositionRef.current = normalizePosition(params.currentProgress);
	}, [params.currentProgress]);

	useEffect(() => {
		if (contentStore.sessionId) {
			sessionIdRef.current = contentStore.sessionId;
		}
		setSelectedSubtitleId(0);
	}, [contentStore.sessionId]);

	useEffect(() => {
		playerStore.params = params;
	}, [params, playerStore]);

	usePlayerControl(setParams);

	useEffect(() => {
		if (params.isReady && !calledOnce.current) {
			let initialPosition = 0;
			const lastWatchedPosition = normalizePosition(coursesStore.lastWatchedCourseItem.position);
			const shouldRestoreLastWatchedPosition =
				coursesStore.lastWatchedCourseItem.id === currentCourseItemId && typeof lastWatchedPosition === "number";

			if (segment) {
				initialPosition = normalizePosition(segment.startPosition);
			}
			if (shouldRestoreLastWatchedPosition) {
				initialPosition = lastWatchedPosition;
			}
			playerStore.params?.playerRef?.current?.seekTo(initialPosition, "seconds");
			setParams((prevState: ControlParams) => ({ ...prevState, currentProgress: initialPosition }));
			calledOnce.current = true;
		}
	}, [
		params.isReady,
		playerStore.params?.playerRef,
		segment,
		currentCourseItemId,
		coursesStore.lastWatchedCourseItem.id,
		coursesStore.lastWatchedCourseItem.position,
	]);

	useEffect(() => {
		if (
			segment &&
			normalizePosition(params.currentProgress) >= normalizePosition(segment.endPosition) &&
			!calledOnceTwo.current
		) {
			playerStore.params?.handleComplete?.();
			calledOnceTwo.current = true;
		}
	}, [params.currentProgress, playerStore.params, segment]);

	useEffect(() => {
		setParams((prevState: ControlParams) => ({
			...prevState,
			isDownload: content.downloadable,
			handleDownload: content.downloadable ? downloadFile : undefined,
		}));
	}, [content.downloadable, downloadFile]);

	useEffect(
		() => {
			return () => {
				void sendEndEventOnce();
			};
		}, //eslint-disable-next-line
		[]
	);

	if (isEmpty(content.fileContent?.url)) return <div className={styles.player_video_box__nodata}>No data</div>;

	return (
		<div className={classNames(styles.player_video_box, className)}>
			<PlayerVideo params={params} setParams={setParams} content={content}>
				{isFullscreen && (
					<PlayerControl
						params={params}
						segment={segment}
						zoomLimitMultiplier={2}
						onFullscreenChange={(value) => setIsFullscreen(value)}
						tracks={content.contentAssets}
						selectedSubtitleId={selectedSubtitleId}
						setSelectedSubtitleId={setSelectedSubtitleId}
					/>
				)}
			</PlayerVideo>
			{!isFullscreen && (
				<PlayerControl
					params={params}
					segment={segment}
					zoomLimitMultiplier={2}
					onFullscreenChange={(value) => setIsFullscreen(value)}
					tracks={content.contentAssets}
					selectedSubtitleId={selectedSubtitleId}
					setSelectedSubtitleId={setSelectedSubtitleId}
				/>
			)}
		</div>
	);
};

export default PlayerVideoBox;
